import React, { useState } from 'react';
import useSelectionStore from '@store/useSelectionStore';
import useGameStore from '@store/useGameStore';
import { ClickDTO } from '@models/ClickDTO';
import { ClickableType } from '@utils/enums';
import { getUnitState, shouldApplyStateColorConversion } from '@utils/unitStates';
import { CONFIG, TILE_SIZE } from '@utils/constants';
import './UnitIcon.css';
import '@styles/unitStates.css'; // ✅ Import state styling

/**
 * Convert unit template name to slug format
 * Example: "Swordsman" -> "swordsman", "Heavy Knight" -> "heavy-knight"
 */
const getUnitSlug = (name) => {
  if (!name) return '';
  return name.toLowerCase().replace(/\s+/g, '-');
};

/**
 * Get dynamic unit image path
 * @param {string} slug - Unit slug (e.g., "swordsman")
 * @param {string} size - Icon size ('small', 'big', or 'map')
 * @returns {string} Path to image
 */
const getUnitImagePath = (slug, size) => {
  let imageSize;

  if (size === 'small') {
    imageSize = 'small';
  } else if (size === 'map') {
    imageSize = 'map';
  } else {
    imageSize = 'medium';
  }

  return `/images/units/${slug}_${imageSize}.svg`;
};

/**
 * ✅ CINEMATIC-AWARE - Muestra el ícono de una unidad con estado selected y cinematográfico
 * @param {object} unit - El objeto de la unidad normalizada.
 * @param {string|number} size - 'small', 'big', or a number in pixels
 * @param {boolean} forceSelected - Forzar estado selected (opcional)
 * @param {boolean} showInitiative - Mostrar badge de orden de iniciativa (solo en mapa)
 * @param {boolean} clickable - Si el icono es clickeable (default: true)
 */
function UnitIcon({ unitId, size = 'small', forceSelected = false, showInitiative = false, clickable = true }) {
  const { currentClick, handleClick } = useSelectionStore();
  const [imageError, setImageError] = useState(false);

  // Get smart unit data (switches between normal/cinematic automatically)
  // Using explicit getState() - no subscription, just snapshot
  // This now checks both deployed units and available units
  const smartUnit = useGameStore.getState().getUnitOrAvailableUnitById(unitId);

  if (!smartUnit) {
    console.warn('[UnitIcon] No unit data provided.');
    return null;
  }

  // Get unit slug and image path
  const unitSlug = getUnitSlug(smartUnit.unit_template?.name);
  const imagePath = getUnitImagePath(unitSlug, size);

  // ✅ Verificar si esta unidad está seleccionada
  const isSelected = forceSelected || (
    currentClick?.type === ClickableType.UNIT &&
    currentClick?.data?.id === smartUnit.id
  );

  // Handle unit click (only if clickable)
  const handleUnitClick = clickable ? (e) => {
    e.stopPropagation(); // ✅ Prevent click from bubbling to HighlightLayer
    const clickDTO = new ClickDTO(
      ClickableType.UNIT,
      smartUnit,
      null,
      {
        position: { x: smartUnit.x, y: smartUnit.y },
        team: smartUnit.team,
        gameId: useGameStore.getState().game?.id
      }
    );
    handleClick(clickDTO);
  } : undefined;

  // ✅ Detectar estado de la unidad usando datos cinematográficos
  const unitState = getUnitState(smartUnit);
  const applyColorConversion = unitState && shouldApplyStateColorConversion(unitState);

  // Get initiative order from unit data (sent by backend)
  const initiativeOrder = smartUnit.initiative_order;

  // Handle image loading error
  const handleImageError = () => {
    setImageError(true);
  };

  // Determine fallback content
  const iconFallback = smartUnit.unit_template?.visual_data?.icon_fallback;
  const shouldShowImage = !imageError && unitSlug;
  const shouldShowFallback = imageError || !unitSlug;

  // Determine if size is numeric or a preset
  const isNumericSize = typeof size === 'number';
  const isMapSize = size === 'map';
  const sizeClass = isNumericSize ? '' : `size-${size}`;

  // Calculate font size based on unit size
  const fontSize = isNumericSize ? Math.floor(size * 0.5) : undefined;

  // ✅ Construimos las clases CSS dinámicamente incluyendo selected y estado
  const classNames = [
    'unit-icon',
    'unit-container', // ✅ Added for state indicator positioning
    sizeClass, // Only add size class if not numeric
    `team-${smartUnit.teamClass}`, // Always apply team color
    isSelected ? 'selected' : '',
    applyColorConversion ? `unit-state-${unitState}` : '' // ✅ Add state-based styling
  ].filter(Boolean).join(' ');

  // Build inline styles for numeric size or map size
  const inlineStyles = isNumericSize ? {
    width: `${size}px`,
    height: `${size}px`,
    fontSize: `${fontSize}px`,
    ...(clickable ? {} : { pointerEvents: 'none' })
  } : isMapSize ? {
    '--unit-tile-size': `${TILE_SIZE}px`,
    ...(clickable ? {} : { pointerEvents: 'none' })
  } : clickable ? {} : { pointerEvents: 'none' };

  // ✅ Debug logging for state detection
  if (unitState && CONFIG.DEBUG_MODE) {
    /*console.log(`[UnitIcon] Unit ${smartUnit.id} state detected:`, {
      originalHp: unit?.hp,
      currentHp: smartUnit.hp,
      state: unitState,
      showOnMap: showStateOnMap,
      applyColor: applyColorConversion,
      usingSmartUnit: !!smartUnit
    });*/
  }

  return (
    <div
      className={classNames}
      style={inlineStyles}
      onClick={handleUnitClick}
      data-unit-id={smartUnit.id}
      data-team={smartUnit.teamClass}
      data-selected={isSelected}
      data-unit-state={unitState || 'alive'} // ✅ For debugging and CSS targeting
      data-clickable={clickable}
    >
      {shouldShowImage ? (
        // Try to load dynamic unit image
        <img
          src={imagePath}
          alt={smartUnit.name}
          className="unit-icon-sprite"
          onError={handleImageError}
        />
      ) : iconFallback ? (
        // Show fallback emoji if available
        <span className="unit-icon-letter">
          {iconFallback}
        </span>
      ) : (
        // Final fallback: show initials
        <span className="unit-icon-letter">
          {(smartUnit.unit_template?.name ?? '').slice(0, 2).toUpperCase()}
        </span>
      )}

      {/* Initiative order badge - only show on map */}
      {showInitiative && initiativeOrder && (
        <div className={`initiative-badge team-${smartUnit.teamClass}`}>
          <span className="initiative-number">{initiativeOrder}</span>
        </div>
      )}
    </div>
  );
}

export default UnitIcon;

